import React, { useContext, useEffect, useState } from "react";
import { RotatingLines } from 'react-loader-spinner';
import { cloneDeep } from 'lodash';
import { EmpdContext, EmpdProvider } from "../../../../../Context";
import DashboardNavigationBar from "./DashboardNavigationBar";
import WelcomeBanner from "./WelcomeBanner";
import ContentWrapper from "../../../../../Components/ContentWrapper";
import Paper from '@mui/material/Paper';
import NCTTable from '../../../../../Components/NCTTable';
import NCTTableColumn from '../../../../../Interfaces/NCTTableColumn';
import HeaderCell from '../../../../../Interfaces/HeaderCell';
import VirtualizedSelect from '../../../../../Components/VirtualizedSelect';
import DashboardCard from './DashboardCard'
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser"
import MsalLoadingComponent from "../../../../../Components/Msal/MsalLoadlingComponent"
import NotAuthorized from "../../../../../Components/NotAuthorized";
import { isProvider } from "../../../../../utils";

interface Props {
    classes?: any,
}

const headerCells: HeaderCell[] = [
  {
    id: 'nctId',
    numeric: false,
    disablePadding: false,
    label: 'NCT ID',
    width: '15%',
  },
  {
    id: 'title',
    numeric: false,
    disablePadding: false,
    label: 'Title',
    width: '55%',
  },
  {
    id: 'closestPoint',
    numeric: false,
    disablePadding: false,
    label: 'Location',
    width: '30%',
  },
/*
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
    minWidth: 170,
  },
*/
]

const columns: NCTTableColumn[] = [
  { id: 'nctId', 
    label: 'NCT ID', 
    format: (value: number | string) => <a href={`https://clinicaltrials.gov/study/${value}`} target='_blank' rel='noreferrer'>{value}</a>
  },
  { id: 'title', 
    label: 'Title', 
  },
  {
    id: 'closestPoint',
    label: 'Location',
  },
/*
  {
    id: 'status',
    label: 'Status',
    align: 'right',
    format: (value: number | string) => value.toLocaleString('en-US'),
  },
*/
];

const renderClinicalTrials = (classes: any, clinicalTrials: any | null, countClinicalTrials: number | null, state: string, country: string, selectedChoice: any, setSelectedChoice:(choice: any) => void) => {

    let trials = cloneDeep(clinicalTrials.trials)
    trials = trials.map((trial: any) => {
        if( trial.closestPoint ) {
            return ({
                ...trial, 
                title: <>
                    <h3>{trial.title}</h3>
                    {trial.briefSummary}
                </>,
                closestPoint: <>
                    {trial.closestPoint.facility}
                    <br/>
                    {trial.closestPoint.city}, {trial.closestPoint.state ? trial.closestPoint.state : trial.closestPoint.country}
                    <br/>
                    {trial.additionalLocations > 0 ? `${trial.additionalLocations} additional locations` : ''}
                </>
            })
        } else {
            return ({...trial}) 
        }
    })

    return (
        <div className={classes.dashboardWrapper}>
            <ContentWrapper>
                <div className='row'>
                    {`There are ${countClinicalTrials} clinical trials currently listed near ${state ? state  + ', ' : ''} ${country ? country : ''}`}
                    <VirtualizedSelect
                        multiple={false}
                        sx={{
                            width: 500,
                            "& .MuiOutlinedInput-root": {
                                color: "#002F6C",
                                // Class for the border around the input field
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#002F6C",
                                    borderWidth: "2px",
                                },
                                "& .MuiOutlineInput-focused": {
                                    color: "#108de0",
                                },
                            },
                            "& .MuiChip-root": {
                                color: "#002F6C",
                            },
                            // Class for the label ofthe input field
                            "& .MuiInputLabel-outlined": {
                                color: "#777777",
                            },
    
                        }}
                        options={[
                            {label: 'State', value: 'State'}, 
                            {label: 'Country', value: 'Country'}, 
                        ]}
                        onChange={(event: React.SyntheticEvent, value: any) => {
                            setSelectedChoice(value)
                        }}
                        value={selectedChoice}
                        label={'Trials in my...'}
                    />
                </div>
                <div className={classes.dashboardContent}>
                    <Paper style={{width: '100%'}}>
                        <NCTTable rows={trials} columns={columns} headerCells={headerCells} />
                    </Paper>
                </div>
            </ContentWrapper>
        </div>
    )
}

const renderHeader = (classes: any, isLoading: boolean, clinicalTrials: any | null, countClinicalTrials: number | null, patientResult: any, selectedChoice:any, setSelectedChoice:(choice: any) => void, schemaName: string | undefined, demoTable: string | undefined) => {

    let state: string = ''
    let country: string = ''

    if( Object.keys(patientResult).length > 0 ) {
        const generalDemo = patientResult.categories['General Demography'].records[0].columns
        state = generalDemo.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.state`).value    
        country = generalDemo.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.country`).value    
    }

    return (
        <div>
            <DashboardNavigationBar />
            <WelcomeBanner />
                <DashboardCard
                    title={'Clinical Trials'}
                    className={classes.dashboardCard}
                >
                    {isLoading && (
						<div className='center'>
	                        <RotatingLines
    	                      strokeColor="#152E54"
        	                  strokeWidth="3"
            	              animationDuration="1.00"
                	          width="48"
                    	      visible={true}
                        	/>
						</div>
                    )}
                    {!isLoading && clinicalTrials && (
                        <>
                            {renderClinicalTrials(classes, clinicalTrials, countClinicalTrials, state, country, selectedChoice, setSelectedChoice)}
                        </>
                    )}
                    {!isLoading && !clinicalTrials && (
                        <>
                            There are no Clinical Trials to show you at this time
                        </>
                    )}
                </DashboardCard>
        </div>
    );
}

const useMyContext = () => {
    const context = useContext(EmpdContext)
    if( !context ) {
        throw new Error("No provider found for Context")
    }
    return context
}

// This component displays patient data from context
const ClinicalTrials = ({ classes }: Props) => {

    const { isLoading, patientResult, activeMrn, setActiveMrn, fetchPatientData, fetchClinicalTrials, clinicalTrials, countClinicalTrials, schemaName, demoTable, fetchNamespace, conditionSet, user, isPatientLoading } = useMyContext()
    const [selectedChoice, setSelectedChoice] = useState<any>({label: 'State', value: 'State'})

    let state: string | null
    let country: string

    if( Object.keys(patientResult).length > 0 ) {
        const generalDemo = patientResult.categories['General Demography'].records[0].columns
        state = generalDemo.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.state`).value
        country = generalDemo.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.country`).value
    }

    useEffect(() => {
		fetchNamespace()
        if( activeMrn === null ) {
           setActiveMrn(localStorage.getItem('userObjectId') as string)
        } else if( Object.keys(patientResult).length === 0 && conditionSet !== undefined) {
            fetchPatientData(activeMrn)
        } else if( state !== null && country !== undefined ) {
            fetchClinicalTrials(state, country, true, null)
        }
    }, [])
    
    useEffect(() => {
        if( state !== null && country !== undefined ) {
            fetchClinicalTrials(state, country, true, null)
        } else if( activeMrn !== null && conditionSet !== undefined) {
            fetchPatientData(activeMrn)
        }

    },[activeMrn, conditionSet])
    
    useEffect(() => {
        if( state !== null && country !== undefined) {
            if( selectedChoice.value === 'Country' ) {
                fetchClinicalTrials(state, country, false, null)
            } else {
                
                fetchClinicalTrials(state, country, true, null)
            }

        } 
    }, [patientResult, selectedChoice])

    return (
			
		<MsalAuthenticationTemplate 
			interactionType={InteractionType.Redirect}
			errorComponent={NotAuthorized}
	        loadingComponent={MsalLoadingComponent}
		>
			{isLoading && !user && Object.keys(patientResult).length === 0 ?
				<div className='center'>
					<RotatingLines
    	                strokeColor="#152E54"
        	            strokeWidth="3"
            	        animationDuration="1.0"
                	    width="48"
                	/>
				</div>
			: !isLoading && isPatientLoading && isProvider(user) ? (
				<NotAuthorized />
				) :
		        <EmpdProvider>
    		        {renderHeader(classes, isLoading, clinicalTrials, countClinicalTrials, patientResult, selectedChoice, setSelectedChoice, schemaName, demoTable)}
        		</EmpdProvider>
			}
		</MsalAuthenticationTemplate>
    );
};

export default ClinicalTrials;
